<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { JobPickingStatus } from '@/components/chips/PickingStatusChip.vue';
import type { HeadersData } from '@/composables/useTable';

import { computed } from 'vue';

import TableActionBtn from '@/components/btns/TableActionBtn.vue';
import PickingStatusChip from '@/components/chips/PickingStatusChip.vue';
import JobAttributeCollection from '@/components/collections/JobAttributeCollection.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import useTable from '@/composables/useTable';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  uniqueKey: string;
  jobId: number;
  jobDate: string;
  customerCode?: string;
  driver?: string | number;
  reference1?: string;
  ref1Prompt?: string;
  stopName?: string;
  stopNumber: number;
  suburb?: string;
  state?: string;
  attributes: string[];
  serviceCode: string;
  status?: string;
  isFutile?: boolean;
  pickingStatus?: JobPickingStatus;
}

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<DataItem[]>;
  isStaff?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
  isStaff: false,
});

const emit = defineEmits<{
  editClick: [jobId: number, jobDate: string];
}>();

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

const headersData = computed<HeadersData>(function () {
  if (props.isStaff) {
    return [
      {
        value: 'jobId',
        title: 'Job ID',
      },
      {
        value: 'jobDate',
        title: 'Date',
      },
      {
        value: 'customerCode',
        title: 'Customer',
      },
      {
        value: 'driver',
        title: 'Driver',
      },
      {
        value: 'reference1',
        title: 'Reference 1',
      },
      {
        value: 'stopName',
        title: 'Stop name',
      },
      {
        value: 'stopNumber',
        title: 'Stop #',
      },
      {
        value: 'suburb',
        title: 'Suburb',
      },
      {
        value: 'state',
        title: 'State',
      },
      {
        value: 'attributes',
        title: 'Attributes',
      },
      {
        value: 'serviceCode',
        title: 'Service code',
      },
      {
        value: 'isFutile',
        title: 'Futile',
      },
      {
        value: 'status',
        title: 'Status',
      },
      {
        value: 'actions',
        title: 'Actions',
        isNotSortable: true,
      },
    ];
  } else {
    return [
      {
        value: 'jobId',
        title: 'Job ID',
      },
      {
        value: 'jobDate',
        title: 'Date',
      },
      {
        value: 'reference1',
        title: 'Reference 1',
      },
      {
        value: 'stopName',
        title: 'Stop name',
      },
      {
        value: 'stopNumber',
        title: 'Stop #',
      },
      {
        value: 'suburb',
        title: 'Suburb',
      },
      {
        value: 'state',
        title: 'State',
      },
      {
        value: 'attributes',
        title: 'Attributes',
      },
      {
        value: 'serviceCode',
        title: 'Service code',
      },
      {
        value: 'isFutile',
        title: 'Futile',
      },
      {
        value: 'status',
        title: 'Status',
      },
    ];
  }
});

const foldColspan = computed(() => {
  return headersData.value.length;
});
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.stickyHeader,
      $style.advancedSearchTable,
    ]"
    data-testid="advancedSearchTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-if="props.isStaff">
        <template v-for="dataItem in sortedData" :key="dataItem.uniqueKey">
          <tr>
            <td>
              <div :class="$style.jobIdCell">
                <PickingStatusChip
                  v-if="dataItem.pickingStatus === 'issue'"
                  :status="dataItem.pickingStatus"
                />
                <JobIdAndIconLinkUnit
                  :job-id="dataItem.jobId"
                  :job-date="dataItem.jobDate"
                />
              </div>
            </td>
            <td>
              {{ dataItem.jobDate }}
            </td>
            <td>
              {{ dataItem.customerCode }}
            </td>
            <td>
              {{ dataItem.driver }}
            </td>
            <td>
              <v-lazy>
                <span>
                  <KParentTooltip
                    v-if="dataItem.ref1Prompt"
                    location="bottom"
                    :offset="3"
                  >
                    {{ dataItem.ref1Prompt }}
                  </KParentTooltip>
                  {{ dataItem.reference1 }}
                </span>
              </v-lazy>
            </td>
            <td>
              {{ dataItem.stopName }}
            </td>
            <td>
              {{ dataItem.stopNumber }}
            </td>
            <td>
              {{ dataItem.suburb }}
            </td>
            <td>
              {{ dataItem.state }}
            </td>
            <td>
              <v-lazy>
                <!-- TODO readonly descendant props -->
                <!-- @vue-ignore -->
                <JobAttributeCollection :attributes="dataItem.attributes" />
              </v-lazy>
            </td>
            <td>
              {{ dataItem.serviceCode }}
            </td>
            <td>
              <span v-if="dataItem.isFutile" class="text-error">Yes</span>
              <span v-else>No</span>
            </td>
            <td>
              {{ dataItem.status }}
            </td>
            <td>
              <div :class="$style.actionBtns">
                <TableActionBtn
                  color="green"
                  icon="mdi-pencil-outline"
                  @click="emit('editClick', dataItem.jobId, dataItem.jobDate)"
                >
                  Edit
                </TableActionBtn>
              </div>
            </td>
          </tr>
        </template>
      </template>
      <template v-else>
        <template v-for="dataItem in sortedData" :key="dataItem.uniqueKey">
          <tr>
            <td>
              <JobIdAndIconLinkUnit
                :job-id="dataItem.jobId"
                :job-date="dataItem.jobDate"
              />
            </td>
            <td>
              {{ dataItem.jobDate }}
            </td>
            <td>
              <v-lazy>
                <span>
                  <KParentTooltip
                    v-if="dataItem.ref1Prompt"
                    location="bottom"
                    :offset="3"
                  >
                    {{ dataItem.ref1Prompt }}
                  </KParentTooltip>
                  {{ dataItem.reference1 }}
                </span>
              </v-lazy>
            </td>
            <td>
              {{ dataItem.stopName }}
            </td>
            <td>
              {{ dataItem.stopNumber }}
            </td>
            <td>
              {{ dataItem.suburb }}
            </td>
            <td>
              {{ dataItem.state }}
            </td>
            <td>
              <v-lazy>
                <!-- TODO readonly descendant props -->
                <!-- @vue-ignore -->
                <JobAttributeCollection :attributes="dataItem.attributes" />
              </v-lazy>
            </td>
            <td>
              {{ dataItem.serviceCode }}
            </td>
            <td>
              <span v-if="dataItem.isFutile" class="text-error">Yes</span>
              <span v-else>No</span>
            </td>
            <td>
              {{ dataItem.status }}
            </td>
          </tr>
        </template>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';
@use '@/styles/table.module.scss' as tableStyles;

.advancedSearchTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
  }
}

  .jobIdTh {
    min-width: 100px;
    width: 100px;
  }
  .jobDateTh {
    min-width: 100px;
    width: 100px;
  }
  .customerCodeTh {
    @include tableStyles.column-wide;
  }
  .driverTh {
    @include tableStyles.column-medium;
  }
  .reference1Th {
    @include tableStyles.column-wide;
  }
  .stopNameTh {
    @include tableStyles.column-flexible;
  }
  .stopNumberTh {
    @include tableStyles.column-small;
  }
  .suburbTh {
    @include tableStyles.column-regular;
  }
  .stateTh {
    @include tableStyles.column-medium;
  }
  .attributesTh {
    @include tableStyles.column-regular;
  }
  .serviceCodeTh {
    @include tableStyles.column-regular;
  }
  .isFutileTh {
    @include tableStyles.column-small;
  }
  .statusTh {
    @include tableStyles.column-regular;
  }
  .actionsTh {
    @include tableStyles.actions-column;
  }
    .actionBtns {
      @include tableStyles.action-buttons;
    }
    .jobIdCell {
      display: flex;
      align-items: center;
      /* The chip drops to its own line rather than widening the 100px column;
         JobIdAndIconLinkUnit keeps the id itself unbreakable. */
      flex-wrap: wrap;
      gap: 4px;
    }
</style>
